import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import productImageSrc from "@/assets/auth-product-image.webp";
import productImage2xSrc from "@/assets/auth-product-image@2x.webp";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { storeLanguage } from "@/utils/languageStorage";
import { MEDIA_QUERY } from "@/utils/mediaQueries";

import AuthScreenFooter from "./AuthScreenFooter";
import PropHeroSelectCountry from "./PropHeroSelectCountry";

const layoutStyle = {
  backgroundColor: "var(--cx-bg-layout)",
} as const;

/** Average top, middle and bottom tones of the product image. */
const productImagePlaceholderStyle = {
  backgroundImage: "linear-gradient(#7a7dab, #3d3d66, #242348)",
} as const;

type AuthSplitLayoutProps = {
  children?: ReactNode;
};

/**
 * Shared chrome for every Auth0 screen.
 * Desktop (min-width 1024px): language selector at the top-left of the left
 * column, content centered in that column, logo at the bottom, product image
 * on the right.
 * Below that: logo at the top, content, then the language selector under the
 * card. No product image.
 */
function AuthSplitLayout({ children }: AuthSplitLayoutProps) {
  const { i18n } = useTranslation();
  const isDesktop = useMediaQuery(MEDIA_QUERY.desktop);

  const handleLanguageChange = (event: { language: "en" | "es" }) => {
    storeLanguage(event.language);
    void i18n.changeLanguage(event.language);
  };

  const languageSelect = (
    <PropHeroSelectCountry
      direction={isDesktop ? "bottom" : "top"}
      currentLanguage={i18n.language as "en" | "es"}
      onChange={handleLanguageChange}
    />
  );

  if (!isDesktop) {
    return (
      <div
        className="flex min-h-dvh w-full flex-col items-center pb-6 pt-20"
        style={layoutStyle}
      >
        <AuthScreenFooter className="px-4" />
        <div className="mt-20 flex w-full flex-col items-center gap-12">
          {children}
          {languageSelect}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full" style={layoutStyle}>
      <div className="relative flex min-h-dvh w-1/2 min-w-0 flex-col">
        <div className="absolute left-10 top-12 z-10">{languageSelect}</div>
        <div className="flex flex-1 flex-col justify-center py-8">
          {children}
        </div>
        <AuthScreenFooter className="px-4 pb-12 pt-6" />
      </div>
      <div className="w-1/2 min-w-0">
        {/*
          Imported so Vite resolves the URL from the script (import.meta.url).
          A root path would be requested from the Auth0 page origin.
        */}
        {/*
          Desktop only (mobile never renders it, so never downloads it), so it
          is encoded for quality, per screen density (CX-902):
          - 1x: lossless WebP, shown ~1:1, where artifacts would be visible.
          - 2x (Retina): q95 WebP, sharp at half the bytes of a lossless 1x;
            artifacts are half a CSS pixel there.
          Both are flattened over white: the design's top-left glow comes from
          transparency, which would vanish on the dark theme.
          Default fetch priority: it never blocks the form, and a low priority
          made it pop in late. The gradient matches the image's tones while it
          loads.
        */}
        <img
          src={productImageSrc}
          srcSet={`${productImageSrc} 1x, ${productImage2xSrc} 2x`}
          alt=""
          width={640}
          height={832}
          decoding="async"
          className="sticky top-0 h-dvh w-full object-cover"
          style={productImagePlaceholderStyle}
        />
      </div>
    </div>
  );
}

export default AuthSplitLayout;
